import { get as _get, toNumber as _toNumber } from 'lodash';
import { EntityId, TiktokStoreId } from '../../config/store-config';
import {
    FinanceFees,
    FinanceOrderRecord,
    emptyFees,
    financeRecordId,
    round2,
} from './finance-record';

// TikTok Shop → FinanceOrderRecord. Pure: no Angular, no injection, no clock.
//
// Source is /finance/202309/statements/{id}/statement_transactions. Two things
// about that call are worth knowing before using it:
//
//   * `sort_field` is REQUIRED. Omitting it returns 400 / code 36009004 with
//     the message "SortField is a required field and has not been provided" —
//     a hard failure, not a defaulted value.
//   * A statement holds rows of several `type`s. Only `ORDER` rows are sales;
//     the rest are statement-level movements and are skipped here.

const num = (source: unknown, path: string): number => {
    const value = _get(source, path);
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const parsed = _toNumber(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

// TikTok's own `fee_amount` is treated as the authoritative total and the
// itemized fields as a partial explanation of it — not the other way round.
//
// That is a measured decision, not caution. Across all 18 rows of statement
// 7657784597237450514 this identity held to the satang:
//
//     settlement_amount = revenue_amount + fee_amount + adjustment_amount
//
// while the fields TikTok itemizes accounted for only part of `fee_amount`.
// The unexplained remainder ran 10–14% of revenue — far too large to be
// rounding, and it is not exposed under any transaction-level field:
//
//     rev  30 | fee_amount  -7.12 | itemized  -3.00 | residual  -4.12
//     rev 120 | fee_amount -22.26 | itemized  -8.99 | residual -13.27
//     rev 420 | fee_amount -94.74 | itemized -50.96 | residual -43.78
//
// Building the total by summing the itemized fields would therefore understate
// TikTok's fees by roughly half and quietly overstate margin. Instead the
// residual is carried explicitly in `fees.other`, so the record always
// reconciles to the settlement while preserving the breakdown that does exist.
// If TikTok later exposes the missing categories, `other` shrinks toward zero
// on its own and nothing downstream has to change.
export const normalizeTiktokTransaction = (
    transaction: unknown,
    context: {
        storeId: TiktokStoreId;
        entityId: EntityId;
        // From the parent statement — the transaction rows carry no settlement
        // time of their own.
        statementTime: number | null;
        // The parent statement, which is the record's settlement slice.
        statementId: string | null;
        fetchedAt: number;
        syncedAt: number;
    },
): FinanceOrderRecord | null => {
    if (String(_get(transaction, 'type') ?? '') !== 'ORDER') {
        return null;
    }

    const orderId = String(_get(transaction, 'order_id') ?? '').trim();
    if (!orderId) {
        return null;
    }

    // TikTok signs costs negative. Negate into buckets that hold costs positive.
    const neg = (path: string): number => -num(transaction, path);

    const fees: FinanceFees = {
        ...emptyFees(),
        commission: neg('platform_commission_amount'),
        transaction: neg('transaction_fee_amount'),
        service:
            neg('referral_fee_amount') +
            neg('fbt_fulfillment_fee_amount') +
            neg('refund_administration_fee_amount'),
        affiliate:
            neg('affiliate_commission_amount') +
            neg('affiliate_partner_commission_amount') +
            neg('affiliate_ads_commission_amount'),
        // Courier cost less TikTok's shipping subsidy. Nets to zero on orders
        // where TikTok funded the delivery, which is most of them.
        shippingBorne:
            neg('actual_shipping_fee_amount') +
            neg('platform_shipping_fee_discount_amount') +
            neg('shipping_fee_subsidy_amount') +
            neg('shipping_cost_discount_amount'),
    };

    // Authoritative total, then the residual assigned to `other` so the record
    // ties to the settlement by construction rather than by luck.
    const reportedTotal = -num(transaction, 'fee_amount');
    const itemized =
        fees.commission +
        fees.transaction +
        fees.service +
        fees.affiliate +
        fees.shippingBorne;
    fees.other = round2(reportedTotal - itemized);
    fees.total = round2(reportedTotal);

    const revenue = num(transaction, 'revenue_amount');

    // A statement id is always present on the live API; the fallbacks keep
    // a malformed statement from collapsing every order into one slice.
    const part =
        context.statementId ||
        (context.statementTime ? `t${context.statementTime}` : 'no-statement');

    return {
        // One document per (order, statement). TikTok settles a sale and its
        // later refund or adjustment in different statements, and keying on
        // the order alone let the later statement overwrite the sale — 2026
        // data held a ฿30 January sale replaced by its February refund row.
        // `orderSn` still names the order, so rows join to billing requests
        // on (storeId, orderSn) as before.
        _id: financeRecordId(context.storeId, orderId, part),
        storeId: context.storeId,
        platform: 'tiktok',
        entityId: context.entityId,
        orderSn: orderId,
        part,
        currency: String(_get(transaction, 'currency') ?? 'THB'),
        detailLevel: 'order-fees',

        orderDate: num(transaction, 'order_create_time') || null,
        // Order creation IS the tax point. See the note in normalize-shopee.ts
        // for why that is the safe reading of §78 rather than a shortcut.
        taxPointDate: num(transaction, 'order_create_time') || null,
        settlementDate: context.statementTime,

        gross: {
            itemsBeforeDiscount: num(transaction, 'gross_sales_amount'),
            // Seller-funded: reduces the tax base.
            sellerDiscount: -num(transaction, 'seller_discount_amount'),
            // TikTok-funded: the seller is still made whole, so this stays IN
            // the tax base (plan §4.6). Kept separate from sellerDiscount and
            // never netted against it.
            platformSubsidy:
                -num(transaction, 'platform_discount_amount') +
                -num(transaction, 'platform_refund_subsidy_amount'),
            buyerShippingPaid: num(
                transaction,
                'customer_paid_shipping_fee_amount',
            ),
            buyerTotal: num(transaction, 'customer_payment_amount'),
            sellerRevenue: revenue,
            // Seller side: gross sales less the seller's own discount, plus
            // shipping the buyer paid. Equals customer_payment_amount −
            // platform_discount_amount on all 200 tiktok-genlogic rows
            // settled in August 2026; built this way so a new
            // platform-funded field cannot silently leave the base.
            taxableConsideration: round2(
                num(transaction, 'gross_sales_amount') +
                    num(transaction, 'seller_discount_amount') +
                    num(transaction, 'customer_paid_shipping_fee_amount'),
            ),
        },
        fees,
        adjustments: {
            refund: num(transaction, 'customer_order_refund_amount'),
            compensation: 0,
            other: num(transaction, 'adjustment_amount'),
        },
        netSettlement: num(transaction, 'settlement_amount'),

        vat: null,
        feeVat: null,

        raw: {
            source: 'tiktok:statement_transactions',
            fetchedAt: context.fetchedAt,
            payload: transaction,
        },
        syncedAt: context.syncedAt,
    };
};

// The identity TikTok's own numbers satisfy, asserted per row at sync time.
// Verified to hold to the satang on every row of the sample statement; a
// failure means TikTok changed the shape, not that the tolerance is too tight.
export const reconcileTiktok = (
    transaction: unknown,
): { ok: boolean; expected: number; reported: number; drift: number } => {
    const expected =
        num(transaction, 'revenue_amount') +
        num(transaction, 'fee_amount') +
        num(transaction, 'adjustment_amount');
    const reported = num(transaction, 'settlement_amount');
    const drift = round2(expected - reported);
    return { ok: Math.abs(drift) <= 0.01, expected, reported, drift };
};
